import { Receipt } from "lucide-react";
import { Link } from "react-router-dom";
import type { PendingExpense } from "../types";
import {
  Card,
  CardHeader,
  Empty,
  formatCurrency,
  SkeletonRow,
  ViewAllLink,
} from "./_shared";

type Props = {
  expenses: PendingExpense[];
  isLoading?: boolean;
};

export function PendingExpensesWidget({ expenses, isLoading }: Props) {
  const total = expenses.reduce((sum, e) => sum + (e.amount || 0), 0);
  const currency = expenses[0]?.currency ?? "AED";

  return (
    <Card>
      <CardHeader
        icon={
          <Receipt
            className="h-4 w-4 text-[hsl(35,90%,35%)]"
            strokeWidth={1.5}
          />
        }
        title="Pending Expenses"
        count={expenses.length}
        accent={expenses.length > 0 ? "hsl(35,90%,35%)" : undefined}
        action={<ViewAllLink href="/expenses?status=pending" />}
      />
      {isLoading ? (
        <div>
          {[0, 1, 2, 3].map((i) => (
            <SkeletonRow key={i} />
          ))}
        </div>
      ) : expenses.length === 0 ? (
        <Empty
          icon={
            <Receipt
              className="h-8 w-8 text-muted-foreground opacity-40"
              strokeWidth={1.5}
            />
          }
          text="No pending expenses"
        />
      ) : (
        <>
          <ul className="divide-y" style={{ borderColor: "hsl(210,30%,92%)" }}>
            {expenses.slice(0, 5).map((e) => (
              <li key={e.id}>
                <Link
                  to={`/expenses/${e.id}`}
                  className="flex items-center gap-2 py-2 hover:bg-[hsl(210,50%,98%)]"
                >
                  {e.category && (
                    <span
                      className="shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium"
                      style={{
                        background: "hsl(210,16%,93%)",
                        color: "hsl(215,16%,40%)",
                      }}
                    >
                      {e.category}
                    </span>
                  )}
                  <span className="truncate text-[13px] font-medium text-foreground">
                    {e.title}
                  </span>
                  <span className="ml-auto shrink-0 text-[13px] font-semibold text-foreground">
                    {formatCurrency(e.amount, e.currency)}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
          <div
            className="mt-2 flex items-center justify-between border-t pt-2 text-[13px]"
            style={{ borderColor: "hsl(210,30%,90%)" }}
          >
            <span className="text-muted-foreground">Total pending</span>
            <span className="font-bold text-foreground">
              {formatCurrency(total, currency)}
            </span>
          </div>
        </>
      )}
    </Card>
  );
}
